/**
 * Hook principale della dashboard Bolletto.
 *
 * Organizzazione stato:
 * - bills / analyses    → dati dal server
 * - config              → impostazioni utente (lead, tipo cliente, target)
 * - ui                  → stato UI transiente (folder aperti, selezioni, loading)
 * - analysis            → analisi corrente visualizzata
 * - password            → stato modale cambio password
 *
 * Gli handler sono raggruppati per dominio.
 */
import { useState, useCallback, useRef, useMemo, useEffect, useReducer } from 'react';
import { useDropzone } from 'react-dropzone';
import * as api from '../services/analysisService';
import { downloadSinglePdf, downloadBatchPdf } from '../features/pdf/pdfExport';
import { playReadyDing } from '../utils/sound';
import { useAuth } from '../contexts/AuthContext';

// ─── Reducer per stato UI complesso ──────────────────────

/**
 * @typedef {Object} UIState
 * @property {Object<string, boolean>} openFolders - cartelle bollette aperte
 * @property {Object<string, boolean>} openHistoryFolders - cartelle storico aperte
 * @property {Set<string>} selectedHistoryIds - ID analisi selezionate per batch
 * @property {boolean} batchDownloading
 * @property {boolean} batchDeleting
 * @property {boolean} downloading - PDF singolo in download
 * @property {string|null} analyzing - ID bolletta in analisi
 * @property {boolean} uploading
 * @property {boolean} updatingCte
 */
const initialUIState = {
  openFolders: {},
  openHistoryFolders: {},
  selectedHistoryIds: new Set(),
  batchDownloading: false,
  batchDeleting: false,
  downloading: false,
  analyzing: null,
  uploading: false,
  updatingCte: false,
};

function uiReducer(state, action) {
  switch (action.type) {
    case 'TOGGLE_FOLDER':
      return { ...state, openFolders: { ...state.openFolders, [action.key]: !state.openFolders[action.key] } };
    case 'OPEN_FOLDER':
      return { ...state, openFolders: { ...state.openFolders, [action.key]: true } };
    case 'TOGGLE_HISTORY_FOLDER':
      return { ...state, openHistoryFolders: { ...state.openHistoryFolders, [action.key]: !state.openHistoryFolders[action.key] } };
    case 'TOGGLE_HISTORY_SELECTION': {
      const next = new Set(state.selectedHistoryIds);
      if (next.has(action.id)) next.delete(action.id);
      else next.add(action.id);
      return { ...state, selectedHistoryIds: next };
    }
    case 'TOGGLE_SELECT_ALL_IN_FOLDER': {
      const { ids } = action;
      const allSelected = ids.every((id) => state.selectedHistoryIds.has(id));
      const next = new Set(state.selectedHistoryIds);
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return { ...state, selectedHistoryIds: next };
    }
    case 'CLEAR_SELECTION':
      return { ...state, selectedHistoryIds: new Set() };
    case 'SET_LOADING':
      return { ...state, [action.field]: action.value };
    default:
      return state;
  }
}

// ─── Raggruppamento per lead source ──────────────────────

/**
 * Raggruppa un array di oggetti per `lead_source`.
 * @param {Array} items - bills o analyses
 * @param {string} itemsKey - nome della proprietà nel gruppo ('bills' o 'analyses')
 * @param {string[]} orderedKeys - ordine desiderato delle chiavi lead_source (dal DB)
 * @param {Object<string,string>} labels - mappa value→label (dal DB)
 * @returns {Array<{key: string, label: string, [itemsKey]: Array}>}
 */
function groupByLeadSource(items, itemsKey, orderedKeys, labels) {
  const groups = {};
  items.forEach((item) => {
    const src = item.lead_source || 'non_assegnato';
    if (!groups[src]) groups[src] = [];
    groups[src].push(item);
  });

  const sorted = [];
  orderedKeys.forEach((key) => {
    if (groups[key]) sorted.push({ key, label: labels[key] || key, [itemsKey]: groups[key] });
  });
  if (groups['non_assegnato']) {
    sorted.push({ key: 'non_assegnato', label: 'Non assegnato', [itemsKey]: groups['non_assegnato'] });
  }
  Object.keys(groups).forEach((key) => {
    if (!orderedKeys.includes(key) && key !== 'non_assegnato') {
      sorted.push({ key, label: labels[key] || key, [itemsKey]: groups[key] });
    }
  });
  return sorted;
}

// ─── Hook principale ─────────────────────────────────────

export function useDashboardData() {
  // ── Dati server ──
  const [bills, setBills] = useState([]);
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [leadSources, setLeadSources] = useState([]);

  // ── Configurazione utente ──
  const [customerType, setCustomerType] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [targetSavings, setTargetSavings] = useState('');

  // ── Account corrente ──
  const { user, isAdmin } = useAuth();
  // Account NON-admin (agenzia/dipendente): la provenienza non e' selezionabile e
  // coincide col nome dell'account (usato per raggruppare bollette e storico).
  useEffect(() => {
    if (!isAdmin && user?.name) setLeadSource(user.name);
  }, [isAdmin, user]);

  // Limiti di accesso impostati dall'admin (null = nessun limite / accesso completo).
  const allowedCustomerTypes = useMemo(() => {
    if (isAdmin) return null;
    const a = user?.allowed_customer_types;
    return Array.isArray(a) ? a : null;
  }, [isAdmin, user]);
  // Se e' consentita una sola tipologia, preselezionala per comodita'.
  useEffect(() => {
    if (allowedCustomerTypes && allowedCustomerTypes.length === 1) {
      setCustomerType((prev) => prev || allowedCustomerTypes[0]);
    }
  }, [allowedCustomerTypes]);

  // ── Analisi corrente ──
  const [analysis, setAnalysis] = useState(null);
  const [isHistorical, setIsHistorical] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [availableCtes, setAvailableCtes] = useState([]);
  const [manualCte, setManualCte] = useState('');

  // ── UI transiente (useReducer) ──
  const [ui, dispatchUI] = useReducer(uiReducer, initialUIState);

  // ── Errore globale ──
  const [error, setError] = useState('');

  // ── Ref per il container PDF ──
  const analysisRef = useRef(null);

  // ── Dati derivati (useMemo) ──
  const leadOrder = useMemo(() => leadSources.map((s) => s.value), [leadSources]);
  const leadLabels = useMemo(() => {
    const m = {};
    leadSources.forEach((s) => { m[s.value] = s.label; });
    return m;
  }, [leadSources]);
  const groupedBills = useMemo(() => groupByLeadSource(bills, 'bills', leadOrder, leadLabels), [bills, leadOrder, leadLabels]);
  const groupedAnalyses = useMemo(() => groupByLeadSource(savedAnalyses, 'analyses', leadOrder, leadLabels), [savedAnalyses, leadOrder, leadLabels]);

  // ── Caricamento iniziale ──
  useEffect(() => {
    const load = async () => {
      try {
        const [billsData, analysesData, leadSourcesData] = await Promise.all([
          api.fetchBills(),
          api.fetchAnalyses(),
          api.fetchLeadSources(),
        ]);
        setBills(billsData);
        setSavedAnalyses(analysesData);
        setLeadSources(leadSourcesData);
      } catch (e) {
        console.error('Failed to load initial data:', e);
      }
    };
    load();
  }, []);

  // ── Handler: CTE ──
  const loadAvailableCtes = useCallback(async (serviceType, customerTypeOverride = null) => {
    try {
      const tariffs = await api.fetchTariffs();
      const key = `${serviceType}_${customerTypeOverride || customerType}`;
      setAvailableCtes(tariffs[key] || []);
    } catch (e) {
      console.error('Failed to load CTEs:', e);
    }
  }, [customerType]);

  // ── Polling automatico per bollette in estrazione ──
  // L'upload restituisce subito status="processing"; l'estrazione AI dura ~30-90s in background.
  // Polliamo ogni 5s finché almeno una bolletta è in processing. Quando una bolletta
  // transita da "processing" a "extracted" suoniamo il ding di notifica E auto-apriamo
  // l'analisi (con scroll in alto) — ma SOLO se l'utente non sta gia' lavorando su
  // un'altra analisi, per non interromperlo. Il ding scatta SOLO sulle bollette nuove
  // appena estratte, NON sull'apertura di bollette/analisi esistenti.
  // Ref pattern per leggere lo stato corrente senza ricreare il polling ad ogni render.
  const handleAnalyzeRef = useRef(null);
  const analysisRef2 = useRef(null);
  const customerTypeRef = useRef(null);
  useEffect(() => { analysisRef2.current = analysis; }, [analysis]);
  useEffect(() => { customerTypeRef.current = customerType; }, [customerType]);

  useEffect(() => {
    const hasProcessing = bills.some((b) => b.status === 'processing');
    if (!hasProcessing) return;
    const interval = setInterval(async () => {
      try {
        const fresh = await api.fetchBills();
        const prevById = new Map(bills.map((b) => [b.id, b.status]));
        const transitioned = fresh.filter(
          (b) => prevById.get(b.id) === 'processing' && b.status === 'extracted'
        );
        setBills(fresh);
        if (transitioned.length > 0) {
          playReadyDing();
          // Auto-open: scrolla in alto e apre la prima analisi appena estratta,
          // ma solo se l'utente non sta gia' visualizzando un'analisi (per non
          // interrompere il lavoro in corso) e se ha gia' selezionato customer_type.
          if (!analysisRef2.current && customerTypeRef.current && handleAnalyzeRef.current) {
            const billToOpen = transitioned[0];
            // Scroll fluido in cima per posizionare la colonna destra in vista
            window.scrollTo({ top: 0, behavior: 'smooth' });
            // Apri l'analisi dopo un piccolo delay per dare tempo allo scroll
            setTimeout(() => handleAnalyzeRef.current(billToOpen), 300);
          }
        }
      } catch (_e) { /* silent */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [bills]);

  // ── Handler: Upload ──
  const onDrop = useCallback(async (acceptedFiles) => {
    const pdfFiles = acceptedFiles.filter((f) => f.type === 'application/pdf');
    if (pdfFiles.length === 0) {
      setError('Solo file PDF sono accettati');
      return;
    }
    dispatchUI({ type: 'SET_LOADING', field: 'uploading', value: true });
    setError('');

    let firstUploadedId = null;
    for (const file of pdfFiles) {
      try {
        const data = await api.uploadBill(file, leadSource);
        setBills((prev) => [...prev, data]);
        dispatchUI({ type: 'OPEN_FOLDER', key: leadSource || 'non_assegnato' });
        if (!firstUploadedId) firstUploadedId = data.id;
      } catch (e) {
        setError(`Errore caricamento ${file.name}: ${e.response?.data?.detail || e.message}`);
      }
    }
    dispatchUI({ type: 'SET_LOADING', field: 'uploading', value: false });

    // Scroll fluido alla sezione "Bollette caricate" dove appare la card processing
    // della nuova bolletta. requestAnimationFrame x2 + tiny timeout: garantisce che
    // React abbia renderizzato la nuova card prima dello scroll.
    if (firstUploadedId) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            const target =
              document.querySelector(`[data-testid="bill-item-${firstUploadedId}"]`) ||
              document.querySelector('[data-testid="bills-grouped-list"]');
            target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 50);
        });
      });
    }
  }, [leadSource]);

  const dropzone = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
    noClick: false,
    noKeyboard: false,
  });

  // ── Handler: Analisi ──
  // L'utente sta visualizzando un'analisi VOLATILE (non finalizzata) oppure una FINALIZZATA?
  // Volatile: si vede mentre si "lavora" la bolletta caricata, può essere modificata.
  // Finalizzata: salvata nello storico, sola lettura.
  const isFinalized = !!(analysis && !analysis.is_volatile);

  const handleAnalyze = useCallback(async (bill) => {
    // Sempre apriamo una sessione VOLATILE modificabile. L'utente può finalizzare più
    // varianti della stessa bolletta in sequenza.
    // Fallback: usa il customer_type estratto dalla bolletta se l'utente non ne ha
    // selezionato uno globale (es. riapre una bolletta di ieri senza re-impostare
    // il box di config).
    const effectiveCustomerType = customerType || bill.extracted_data?.customer_type;
    if (!effectiveCustomerType) {
      setError('Seleziona la tipologia cliente prima di analizzare.');
      return;
    }
    dispatchUI({ type: 'SET_LOADING', field: 'analyzing', value: bill.id });
    setError('');
    setAnalysis(null);

    try {
      const data = await api.compareBill(bill.id, effectiveCustomerType, targetSavings);
      if (data.error === 'fixed_price') {
        setError(data.message);
        setAnalysis({ error: data });
        setIsHistorical(false);
      } else {
        setAnalysis({ ...data, is_volatile: true });
        setIsHistorical(false);
        setSelectedBill(bill);
        setManualCte(data.selected_cte);
        loadAvailableCtes(bill.extracted_data?.service_type || 'energia', effectiveCustomerType);
        // NB: NON modifichiamo customerType/leadSource globali — quelli sono il setup
        // OBBLIGATORIO che l'utente compila per ogni NUOVO upload. Per gli handler
        // dipendenti (manual CTE, finalize, inline-edit) usiamo `activeCustomerType`
        // derivato da analysis.customer_type — cosi' una bolletta riaperta usa sempre
        // i SUOI parametri originali, senza inquinare il box di setup.
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((d) => d.msg || JSON.stringify(d)).join(', ') : e.message);
      setError(`Errore analisi: ${msg}`);
    }
    dispatchUI({ type: 'SET_LOADING', field: 'analyzing', value: null });
  }, [customerType, targetSavings, loadAvailableCtes]);

  // Sync ref usato dal polling per auto-aprire l'analisi appena pronta
  // (vedi useEffect di polling sopra). Aggiornato ad ogni render.
  useEffect(() => { handleAnalyzeRef.current = handleAnalyze; }, [handleAnalyze]);

  const handleViewSavedAnalysis = useCallback((savedAnalysis) => {
    setError('');
    setAnalysis({ ...savedAnalysis, is_volatile: false });
    setIsHistorical(true);
    setSelectedBill(null);
  }, []);

  const handleManualCteUpdate = useCallback(async () => {
    if (!manualCte || !selectedBill || isFinalized) return;
    dispatchUI({ type: 'SET_LOADING', field: 'updatingCte', value: true });
    setError('');
    try {
      // VOLATILE: la modifica della CTE non finalizza, l'utente deve cliccare "Finalizza".
      const data = await api.compareBillManual(selectedBill.id, (analysis?.customer_type || customerType), manualCte);
      setAnalysis({ ...data, is_volatile: true });
    } catch (e) {
      setError(`Errore aggiornamento: ${e.response?.data?.detail || e.message}`);
    }
    dispatchUI({ type: 'SET_LOADING', field: 'updatingCte', value: false });
  }, [manualCte, selectedBill, customerType, analysis, isFinalized]);

  // ── Handler: Override consumo annuo ──
  const handleUpdateAnnualConsumption = useCallback(async (newConsumption) => {
    if (!analysis || isFinalized || !selectedBill) return;
    setError('');
    try {
      // VOLATILE: ricalcola con override consumo annuo senza salvare nello storico.
      const cte = analysis.selected_cte;
      const data = await api.compareBillManual(selectedBill.id, (analysis?.customer_type || customerType), cte, {
        annualConsumptionOverride: newConsumption,
      });
      setAnalysis({ ...data, is_volatile: true });
    } catch (e) {
      setError(`Errore aggiornamento consumo annuo: ${e.response?.data?.detail || e.message}`);
      throw e;
    }
  }, [analysis, selectedBill, customerType, isFinalized]);

  // ── Handler: Edit dati estratti (client_name + iva_rate) ──
  // Pattern uguale a handleUpdateAnnualConsumption: salva sulla bolletta,
  // poi rifa il compare volatile per aggiornare la UI.
  const handleEditExtracted = useCallback(async (payload) => {
    if (!selectedBill || isFinalized) return;
    setError('');
    try {
      const updatedBill = await api.editBillExtracted(selectedBill.id, payload);
      // Refresh della bolletta selezionata
      setSelectedBill(updatedBill);
      setBills((prev) => prev.map((b) => (b.id === updatedBill.id ? updatedBill : b)));
      // Refresh dell'analisi volatile col compare aggiornato
      if (analysis) {
        const cte = analysis.selected_cte;
        const annualOverride = analysis.annual_projection?.annual_consumption;
        const data = await api.compareBillManual(selectedBill.id, (analysis?.customer_type || customerType), cte, {
          annualConsumptionOverride: annualOverride,
        });
        setAnalysis({ ...data, is_volatile: true });
      }
    } catch (e) {
      setError(`Errore modifica dati: ${e.response?.data?.detail || e.message}`);
      throw e;
    }
  }, [selectedBill, customerType, analysis, isFinalized]);

  // ── Handler: Finalizza ──
  // Salva l'analisi corrente nello storico ma MANTIENE la sessione volatile editabile,
  // così l'utente può applicare varianti diverse e finalizzarne più di una.
  const handleFinalize = useCallback(async () => {
    if (!analysis || isFinalized || !selectedBill) return;
    dispatchUI({ type: 'SET_LOADING', field: 'finalizing', value: true });
    setError('');
    try {
      const cte = analysis.selected_cte;
      const annualOverride = analysis.annual_projection?.annual_consumption;
      const data = await api.compareBillManual(selectedBill.id, (analysis?.customer_type || customerType), cte, {
        annualConsumptionOverride: annualOverride,
        save: true,
      });
      // Aggiungo allo storico ma RESTO in modalità volatile
      setSavedAnalyses((prev) => [data, ...prev]);
      setAnalysis((prev) => ({ ...(prev || data), is_volatile: true, just_finalized_at: Date.now() }));
    } catch (e) {
      setError(`Errore finalizzazione: ${e.response?.data?.detail || e.message}`);
    }
    dispatchUI({ type: 'SET_LOADING', field: 'finalizing', value: false });
  }, [analysis, selectedBill, customerType, isFinalized]);

  // ── Handler: Eliminazione ──
  const handleDeleteBill = useCallback(async (billId) => {
    try {
      await api.deleteBill(billId);
      setBills((prev) => prev.filter((b) => b.id !== billId));
      if (selectedBill?.id === billId) {
        setSelectedBill(null);
        setAnalysis(null);
      }
    } catch (e) {
      setError(`Errore eliminazione: ${e.response?.data?.detail || e.message}`);
    }
  }, [selectedBill]);

  const handleBatchDelete = useCallback(async () => {
    if (ui.selectedHistoryIds.size === 0) return;
    if (!window.confirm(`Eliminare ${ui.selectedHistoryIds.size} analisi selezionate?`)) return;
    dispatchUI({ type: 'SET_LOADING', field: 'batchDeleting', value: true });
    try {
      await api.batchDeleteAnalyses(Array.from(ui.selectedHistoryIds));
      setSavedAnalyses((prev) => prev.filter((a) => !ui.selectedHistoryIds.has(a.id)));
      if (analysis && ui.selectedHistoryIds.has(analysis.id)) {
        setAnalysis(null);
        setIsHistorical(false);
      }
      dispatchUI({ type: 'CLEAR_SELECTION' });
    } catch (e) {
      setError(`Errore eliminazione: ${e.response?.data?.detail || e.message}`);
    }
    dispatchUI({ type: 'SET_LOADING', field: 'batchDeleting', value: false });
  }, [ui.selectedHistoryIds, analysis]);

  // ── Handler: PDF ──
  const handleDownloadPdf = useCallback(async () => {
    if (!analysis || !analysisRef.current) return;
    dispatchUI({ type: 'SET_LOADING', field: 'downloading', value: true });
    try {
      await downloadSinglePdf(analysisRef.current, analysis);
    } catch (e) {
      console.error('PDF generation error:', e);
      setError('Errore nella generazione del PDF');
    }
    dispatchUI({ type: 'SET_LOADING', field: 'downloading', value: false });
  }, [analysis]);

  /**
   * Batch PDF — rendering off-screen deterministico.
   * Non manipola più lo stato UI visibile (setAnalysis/setIsHistorical).
   * Ogni analisi viene renderizzata in un container off-screen isolato.
   * Politica errori: skip con log, il PDF contiene le analisi riuscite.
   */
  const handleBatchPdf = useCallback(async () => {
    if (ui.selectedHistoryIds.size === 0) return;
    const defaultName = `Bolletto_Analisi_Multiple_${ui.selectedHistoryIds.size}`;
    const fileName = window.prompt('Nome del file PDF:', defaultName);
    if (!fileName) return;

    dispatchUI({ type: 'SET_LOADING', field: 'batchDownloading', value: true });

    try {
      const selectedAnalyses = savedAnalyses.filter((a) => ui.selectedHistoryIds.has(a.id));
      const result = await downloadBatchPdf({ selectedAnalyses, fileName });

      if (result.failed > 0) {
        setError(`PDF generato con ${result.exported} analisi. ${result.failed} fallite: ${result.errors.join(', ')}`);
      }
      // Pulisce la selezione dopo un batch riuscito (almeno 1 export) per evitare
      // doppi download accidentali e dare conferma visiva dell'azione completata.
      if (result.exported > 0) {
        dispatchUI({ type: 'CLEAR_SELECTION' });
      }
    } catch (e) {
      console.error('Batch PDF error:', e);
      setError('Errore nella generazione del PDF multiplo');
    }

    dispatchUI({ type: 'SET_LOADING', field: 'batchDownloading', value: false });
  }, [ui.selectedHistoryIds, savedAnalyses]);

  // ── Ritorno ──
  const analyzedBillIds = useMemo(
    () => new Set(savedAnalyses.map((a) => a.bill_id)),
    [savedAnalyses]
  );
  const analysesCountByBillId = useMemo(() => {
    const m = {};
    savedAnalyses.forEach((a) => { m[a.bill_id] = (m[a.bill_id] || 0) + 1; });
    return m;
  }, [savedAnalyses]);

  return {
    // Dati
    bills,
    savedAnalyses,
    leadSources,
    groupedBills,
    groupedAnalyses,
    analyzedBillIds,
    analysesCountByBillId,

    // Configurazione
    customerType, setCustomerType,
    leadSource, setLeadSource,
    targetSavings, setTargetSavings,

    // Analisi corrente
    analysis,
    isHistorical,
    selectedBill,
    availableCtes,
    manualCte, setManualCte,
    analysisRef,

    // Account
    isAdmin,
    accountName: user?.name || '',
    accountRole: user?.role || 'agent',
    allowedCustomerTypes,

    // UI
    ui, dispatchUI,
    error, setError,

    // Dropzone
    dropzone,

    // Handler
    handleAnalyze,
    handleViewSavedAnalysis,
    handleManualCteUpdate,
    handleUpdateAnnualConsumption,
    handleEditExtracted,
    handleFinalize,
    isFinalized,
    handleDeleteBill,
    handleBatchDelete,
    handleDownloadPdf,
    handleBatchPdf,
  };
}
